<template>
  <div class="mic-row generic-border v-centerer" :class="elClasses">
    <div class="legend mic-row__team">
      {{ decodeHTML(team.name) }}
    </div>
    <div class="mic-row__themes-wrapper v-centerer">
      <div
        v-for="(theme, index) in filteredThemes"
        :key="theme.id"
        class="mic-row__theme"
        :style="themeStyle"
      >
        <div class="mic-row__theme__wrapper">
          <div class="h-v-centerer">
            <div>
              <div>{{ theme.name }}</div>
              <div v-if="breakdownPerThemeId[`${theme.id}`]" class="mic-row__theme__ratio">
                <span class="mic-row__theme__ratio__number" :style="neutralColorStyle">
                  {{ breakdownPerThemeId[`${theme.id}`][0] }}
                </span>
                 /
                <span class="mic-row__theme__ratio__number" :style="badColorStyle">
                  {{ breakdownPerThemeId[`${theme.id}`][1] }}
                </span>
                 /
                <span class="mic-row__theme__ratio__number" :style="goodColorStyle">
                  {{ breakdownPerThemeId[`${theme.id}`][2] }}
                </span>
              </div>
            </div>
            <div class="mic-row__theme__doughnut">
              <canvas :ref="`doughnut-theme-${theme.id}`" class="f-h f-w" />
            </div>
          </div>

          <div class="h-v-centerer mic-row__theme__indicators">
            <div v-for="indic in theme.indicators" :key="indic.id" class="mic-row__theme__indicator">
              <div class="f-h f-w">
                <canvas :ref="`pie-indicator-${indic.id}`" class="f-h f-w" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * In management indicators, one row corresponds to one team.
 * This is a compact view of a team's indicators over a given period
 */

import Moment from 'moment-timezone';
import { Chart } from 'chart.js';
import { cloneDeep } from 'lodash';
// Mixins
import mixinCommonColors from '@/mixins/common-colors.vue';
// Utils
import { SColors } from '@/utils/constants';
import { decodeHTML, itemWidthClasses } from '@/utils/main';
import { ChartHelper } from '@/utils/indicator-utils';

export default {

  name: 'ManagementIndicatorsCompactRow',

  mixins: [mixinCommonColors],

  props: {
    team: {
      type: Object,
      required: true,
    },
    customConfig: {
      type: Object,
      required: true,
    },
    range: {
      type: Object,
      validator(value) {
        return value instanceof Object
          && (Moment.isMoment(value.start) && value.start.isValid())
          && (Moment.isMoment(value.end) && value.end.isValid());
      },
      required: true,
    },
    visibleChartRange: {
      type: Object,
      validator(value) {
        return value instanceof Object
          && (Moment.isMoment(value.start) && value.start.isValid())
          && (Moment.isMoment(value.end) && value.end.isValid());
      },
      required: true,
    },
    selected: Boolean,
    displayGwIndicators: Boolean,
  },

  data() {
    return {
      // Charts
      chartsPerIndicatorId: {},
      chartsPerThemeId: {},
      chartHelper: null,
      drawTimeout: null,
      // Chart and ratio colors
      breakdownPerThemeId: {},
      elClasses: null,
    };
  },

  computed: {
    /** Number: Maximum number of themes in a row */
    maxThemesInRow() {
      if (!this.elClasses) {
        return 5;
      }
      if (this.elClasses['sm-small-portrait-size']) {
        return 2;
      }
      if (this.elClasses['sm-portrait-size']) {
        return 3;
      }
      if (this.elClasses['sm-landscape-size']) {
        return 4;
      }
      if (this.elClasses['tablet-size']) {
        return 4;
      }
      return 5;
    },
    /** Object: CSS style of each theme */
    themeStyle() {
      const max = Math.min(this.filteredThemes.length, this.maxThemesInRow);
      return {
        flex: `0 0 ${100 / max}%`,
        width: `${100 / max}%`,
      };
    },
    /** Display configuration of the chart */
    chartConfiguration() {
      const { pointPeriodObj } = this.customConfig;
      return {
        ...this.customConfig,
        pointPeriod: pointPeriodObj.period,
        pointUnit: pointPeriodObj.unit,
        pointStartDay: pointPeriodObj.startDay,
        configStart: this.range.start.clone(),
      };
    },
    /** Array: Team's themes after applying filters */
    filteredThemes() {
      if (this.displayGwIndicators) {
        return this.team.themes.filter(theme => theme.indicators.length > 0);
      }
      const themes = cloneDeep(this.team.themes);
      themes.forEach((theme) => {
        theme.indicators = theme.indicators.filter(indic => indic.isGw === 0);
      });
      return themes.filter(theme => theme.indicators.length > 0);
    },
    /** Object: CSS style for ratio elements */
    goodColorStyle() {
      return { color: SColors.indicatorsGreenLine };
    },
    badColorStyle() {
      return { color: SColors.indicatorsRedLine };
    },
    neutralColorStyle() {
      return { color: SColors.gray };
    },
  },

  watch: {
    team: {
      deep: true,
      handler(newVal, oldVal) {
        clearTimeout(this.drawTimeout);
        this.drawTimeout = setTimeout(this.makeCharts, 50);
      },
    },
  },

  created() {
    $(window).on(this.resizeEventName, this.onSizeChanged);
  },

  mounted() {
    this.chartHelper = new ChartHelper();
    this.drawTimeout = setTimeout(this.makeCharts, 50);
    this.onSizeChanged();
  },

  beforeDestroy() {
    clearTimeout(this.drawTimeout);
    (this.team.themes || []).forEach((theme) => {
      theme.indicators.forEach((indicator) => {
        if (this.chartsPerIndicatorId[`${indicator.id}`]) {
          this.chartsPerIndicatorId[`${indicator.id}`].destroy();
          delete this.chartsPerIndicatorId[`${indicator.id}`];
        }
      });

      if (this.chartsPerThemeId[`${theme.id}`]) {
        this.chartsPerThemeId[`${theme.id}`].destroy();
        delete this.chartsPerThemeId[`${theme.id}`];
      }
    });
  },

  methods: {
    decodeHTML,
    makeCharts() {
      // Chart colors
      const colors = [SColors.gray, SColors.indicatorsRedLine, SColors.indicatorsGreenLine];
      // Chart options
      const options = {
        plugins: {
          tooltip: {
            callbacks: {
              label: (item) => {
                const data = (item.dataset && item.dataset.data) || [0, 0, 0];
                return this.$tc('message.N_EMPTY_VALUES', data[0], { nb: data[0] }) + '<br>'
                  + this.$tc('message.N_VALUES_OUTSIDE', data[1], { nb: data[1] }) + '<br>'
                  + this.$tc('message.N_VALUES_COMPLY', data[2], { nb: data[2] });
              },
            },
          },
        },
        elements: {
          arc: {
            borderWidth: 0,
          },
        },
      };

      (this.team.themes || []).forEach((theme) => {
        // Prepare the data for the theme doughnut
        const themeData = [0, 0, 0];

        theme.indicators.forEach((indicator) => {
          // Make a small pie chart for each indicator (number of good/bad/empty values)
          const elt = this.$refs[`pie-indicator-${indicator.id}`] && this.$refs[`pie-indicator-${indicator.id}`][0];
          const { points } = this.chartHelper.buildCustomConfigChart(
            indicator,
            indicator.inputValues,
            this.chartConfiguration,
            this.team.workingDays,
            this.visibleChartRange,
          );

          const pieData = [0, 0, 0];
          points.forEach((point) => {
            pieData[point.goodBadNeutral + 1]++;
            themeData[point.goodBadNeutral + 1]++;
          });
          if (elt) {
            const data = {
              labels: [indicator.name],
              datasets: [{
                data: pieData,
                backgroundColor: colors,
                hoverBackgroundColor: colors,
                borderWidth: [0, 0, 0],
                hoverBorderWidth: [0, 0, 0],
              }],
            };
            if (this.chartsPerIndicatorId[`${indicator.id}`]) {
              this.chartsPerIndicatorId[`${indicator.id}`].destroy();
              delete this.chartsPerIndicatorId[`${indicator.id}`];
            }
            this.chartsPerIndicatorId[`${indicator.id}`] = new Chart(elt.getContext('2d'), { type: 'pie', data, options });
          }
        });

        // Make a doughnut chart for each theme showing the cumulated numbers of good/bad/empty values of indicators
        const themeElt = this.$refs[`doughnut-theme-${theme.id}`] && this.$refs[`doughnut-theme-${theme.id}`][0];
        if (themeElt) {
          const config = {
            maintainAspectRatio: false,
            type: 'doughnut',
            data: {
              labels: [],
              datasets: [{
                data: themeData,
                backgroundColor: colors,
                hoverBackgroundColor: colors,
                borderWidth: [0, 0, 0],
                hoverBorderWidth: [0, 0, 0],
              }],
            },
            options: {
              cutout: '70%',
            },
          };
          if (this.chartsPerThemeId[`${theme.id}`]) {
            this.chartsPerThemeId[`${theme.id}`].destroy();
            delete this.chartsPerThemeId[`${theme.id}`];
          }
          this.chartsPerThemeId[`${theme.id}`] = new Chart(themeElt.getContext('2d'), config);
        }

        this.$set(this.breakdownPerThemeId, `${theme.id}`, themeData);
      });
    },
    /** Style of the item is determined by the size of $el */
    onSizeChanged() {
      const width = this.$el.offsetWidth;
      this.elClasses = itemWidthClasses
        .reduce((classes, wc) => ({ ...classes, [wc.className]: width <= wc.width }), {});
    },
  },
};
</script>>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.mic-row {
  text-align: left;
  margin-top: .7rem;
  padding: 0 1rem;
  cursor: pointer;

  &:hover {
    background: $main-hover-color;
  }

  &__team {
    flex: 0 0 10rem;
    min-width: 0px;
  }

  &__themes-wrapper {
    flex: 0 0 calc(100% - 10rem);
    min-width: 0px;
    flex-wrap: wrap;
    margin-top: 1rem;
    margin: .7rem 0;
  }

  &__theme {
    padding: 0.2rem 1.5rem;
    box-sizing: border-box;

    &__wrapper {
      border-radius: $main-border-radius;
      background: $color-body-background;
      padding: 0.4rem 0.5rem;
    }

    &__doughnut {
      width: 1.9rem;
      height: 1.9rem;
      margin-left: .7rem;
    }

    &__ratio {
      margin-top: .3rem;

      &__number {
        font-weight: 600;
      }
    }

    &__indicators {
      margin-top: .5rem;
      margin-bottom: .3rem;
    }

    &__indicator {
      width: 1.4rem;
      height: 1.4rem;
      margin-right: .3rem;
    }
  }

  &.sm-landscape-size {
    flex-flow: column;

    .mic-row {
      &__team {
        flex: 0 0 auto;
        padding: 1rem;
      }

      &__themes-wrapper {
        flex: 0 0 auto;
        width: 100%;
      }

      &__theme {
        padding: 0.2rem .8rem;
      }
    }
  }
}

</style>
